import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, differenceInDays, addYears, isBefore } from "date-fns";
import { 
  Calendar, Gift, ShoppingBag, Users, ArrowLeft, Plus, 
  Trash2, Pencil, Crown, Shield, ExternalLink, Search,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CustomerHeader } from "@/components/CustomerHeader";
import { CustomerFooter } from "@/components/CustomerFooter";
import { SEO } from "@/components/SEO";

interface GiftEvent {
  id: string;
  event_name: string;
  recipient_name: string | null;
  event_date: string;
  is_recurring: boolean;
  customer_email: string | null;
  participant_count: number;
  created_at: string;
}

interface GroupPurchase {
  id: string;
  title: string;
  description: string | null;
  organizer_email: string;
  target_date: string | null;
  status: string;
  participant_count: number;
  created_at: string;
}

const SESSION_KEYS = ["shakatah_gift_session", "shakatah_customer_session"];

export default function EventManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [giftEvents, setGiftEvents] = useState<GiftEvent[]>([]);
  const [groupPurchases, setGroupPurchases] = useState<GroupPurchase[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'gift' | 'purchase'; id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = () => {
    for (const key of SESSION_KEYS) {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const session = JSON.parse(stored);
          const email = session.email?.toLowerCase();
          if (email) {
            setUserEmail(email);
            fetchEvents(email);
            return;
          }
        }
      } catch (e) {
        console.error("Session parse error:", e);
      }
    }
    // No valid session found, redirect to login
    navigate("/my-purchases");
  };

  const fetchEvents = async (email: string) => {
    setLoading(true);
    try {
      // Fetch gift events where user is organizer
      const { data: giftData } = await supabase
        .from("gift_reminders")
        .select("*, event_invitees(id)")
        .eq("customer_email", email)
        .order("event_date", { ascending: true });

      const giftEventsWithCounts = (giftData || []).map((event: any) => ({
        ...event,
        participant_count: (event.event_invitees?.length || 0) + 1
      }));
      setGiftEvents(giftEventsWithCounts);

      // Fetch group purchases where user is organizer
      const { data: purchaseData } = await supabase
        .from("group_purchases")
        .select("*, group_purchase_participants(id, status)")
        .eq("organizer_email", email)
        .order("created_at", { ascending: false });

      const purchasesWithCounts = (purchaseData || []).map((purchase: any) => ({
        ...purchase,
        participant_count: (purchase.group_purchase_participants?.filter((p: any) => p.status === 'active')?.length || 0) + 1
      }));
      setGroupPurchases(purchasesWithCounts);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      if (itemToDelete.type === 'gift') {
        const { error } = await supabase
          .from("gift_reminders")
          .delete()
          .eq("id", itemToDelete.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("group_purchases")
          .update({ status: 'cancelled' })
          .eq("id", itemToDelete.id);
        if (error) throw error;
      }
      
      toast.success(`${itemToDelete.name} has been deleted`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      if (userEmail) fetchEvents(userEmail);
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    } finally {
      setIsDeleting(false);
    }
  };

  const getNextOccurrence = (eventDate: string, isRecurring: boolean) => {
    const date = new Date(eventDate);
    const today = new Date();
    
    if (isRecurring) {
      let nextDate = new Date(today.getFullYear(), date.getMonth(), date.getDate());
      if (isBefore(nextDate, today)) {
        nextDate = addYears(nextDate, 1);
      }
      return nextDate;
    }
    return date;
  };

  const getDaysUntil = (eventDate: string, isRecurring: boolean = false) => {
    const nextDate = getNextOccurrence(eventDate, isRecurring);
    return differenceInDays(nextDate, new Date());
  };

  const filteredGiftEvents = giftEvents.filter(event => {
    const matchesSearch = event.event_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.recipient_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const daysUntil = getDaysUntil(event.event_date, event.is_recurring);
    const matchesFilter = filterStatus === "all" || 
      (filterStatus === "upcoming" && daysUntil >= 0 && daysUntil <= 30) ||
      (filterStatus === "future" && daysUntil > 30);
    return matchesSearch && matchesFilter;
  });

  const filteredPurchases = groupPurchases.filter(purchase => {
    const matchesSearch = purchase.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      purchase.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === "all" || 
      (filterStatus === "active" && purchase.status === "active") ||
      (filterStatus === "completed" && purchase.status === "completed");
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CustomerHeader />
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customer" />
        </div>
        <CustomerFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO title="Event Management - Shakatah" description="Manage your group gifts and purchases" />
      <CustomerHeader />

      <main className="flex-1 container pt-16 pb-6 max-w-4xl">
        <Link to="/my-purchases" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          My Dashboard
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6 text-customer" />
              Event Management
            </h1>
            <p className="text-muted-foreground">Manage events you've organized</p>
          </div>
          <Badge variant="outline" className="gap-1">
            <Crown className="w-3 h-3" />
            Organizer View
          </Badge>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="upcoming">Upcoming (30d)</SelectItem>
              <SelectItem value="future">Future</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="gifts" className="space-y-4">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="gifts" className="gap-2">
              <Gift className="w-4 h-4" />
              Group Gifts ({filteredGiftEvents.length})
            </TabsTrigger>
            <TabsTrigger value="purchases" className="gap-2">
              <ShoppingBag className="w-4 h-4" />
              Group Purchases ({filteredPurchases.length})
            </TabsTrigger>
          </TabsList>

          {/* Group Gifts Tab */}
          <TabsContent value="gifts" className="space-y-3">
            {filteredGiftEvents.length === 0 ? (
              <Card className="p-8 text-center">
                <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Group Gifts Found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchQuery ? "Try a different search term" : "You haven't organized any group gifts yet"}
                </p>
              </Card>
            ) : (
              filteredGiftEvents.map((event) => {
                const daysUntil = getDaysUntil(event.event_date, event.is_recurring);
                const isUrgent = daysUntil >= 0 && daysUntil <= 7;
                
                return (
                  <Card key={event.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Gift className="w-4 h-4 text-pink-500" />
                            <h3 className="font-semibold truncate">{event.event_name}</h3>
                            {event.is_recurring && (
                              <Badge variant="secondary" className="text-xs">Recurring</Badge>
                            )}
                          </div>
                          {event.recipient_name && (
                            <p className="text-sm text-muted-foreground">For {event.recipient_name}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(event.event_date), "MMM d, yyyy")}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {event.participant_count} participants
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className={`text-center px-3 py-1 rounded-lg ${isUrgent ? 'bg-pink-500/10 text-pink-600' : 'bg-muted'}`}>
                            <div className="text-lg font-bold">{daysUntil}</div>
                            <div className="text-xs text-muted-foreground">days</div>
                          </div>
                          
                          <div className="flex flex-col gap-1">
                            <Link to={`/events/${event.id}`}>
                              <Button variant="outline" size="sm" className="w-full gap-1">
                                <ExternalLink className="w-3 h-3" />
                                View
                              </Button>
                            </Link>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-destructive border-destructive/30 hover:bg-destructive/10"
                              onClick={() => {
                                setItemToDelete({ type: 'gift', id: event.id, name: event.event_name });
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Group Purchases Tab */}
          <TabsContent value="purchases" className="space-y-3">
            {filteredPurchases.length === 0 ? (
              <Card className="p-8 text-center">
                <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Group Purchases Found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchQuery ? "Try a different search term" : "You haven't organized any group purchases yet"}
                </p>
              </Card>
            ) : (
              filteredPurchases.map((purchase) => {
                const daysUntil = purchase.target_date ? getDaysUntil(purchase.target_date) : null;
                const isUrgent = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
                
                return (
                  <Card key={purchase.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <ShoppingBag className="w-4 h-4 text-blue-500" />
                            <h3 className="font-semibold truncate">{purchase.title}</h3>
                            <Badge 
                              variant={purchase.status === 'active' ? 'default' : 'secondary'}
                              className={purchase.status === 'active' ? 'bg-green-500/10 text-green-600 border-green-500/20' : ''}
                            >
                              {purchase.status}
                            </Badge>
                          </div>
                          {purchase.description && (
                            <p className="text-sm text-muted-foreground line-clamp-1">{purchase.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            {purchase.target_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(purchase.target_date), "MMM d, yyyy")}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {purchase.participant_count} participants
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {daysUntil !== null && (
                            <div className={`text-center px-3 py-1 rounded-lg ${isUrgent ? 'bg-blue-500/10 text-blue-600' : 'bg-muted'}`}>
                              <div className="text-lg font-bold">{daysUntil}</div>
                              <div className="text-xs text-muted-foreground">days</div>
                            </div>
                          )}
                          
                          <div className="flex flex-col gap-1">
                            <Link to={`/purchases/${purchase.id}`}>
                              <Button variant="outline" size="sm" className="w-full gap-1">
                                <ExternalLink className="w-3 h-3" />
                                View
                              </Button>
                            </Link>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-destructive border-destructive/30 hover:bg-destructive/10"
                              onClick={() => {
                                setItemToDelete({ type: 'purchase', id: purchase.id, name: purchase.title });
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </main>

      <CustomerFooter />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{itemToDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All participants will lose access to this event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
